import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';
import {
  BodyText,
  Checkbox,
  SectionHeader,
  TextLinkifyTags,
  theme,
  useBreakpointHelpers,
  VStack,
} from '@sg/garnish';

import { CheckoutLayoutBlock } from '@order/components';

import { useLocalizationContext } from '../../context';
import { SweetpassCheckoutCta } from './SweetpassCheckoutCta';
import type { useSweetpassCheckoutLoadedContent } from './useSweetpassCheckoutLoadedContent';

export const SweetpassCheckoutBlock = ({
  testID,
  content,
  isLoading,
  hasAcceptedTerms,
  handleAcceptTerms,
  handleCta,
}: SweetpassCheckoutBlockProps) => {
  const { t, formatDate, formatPrice } = useLocalizationContext();
  const { isMobile } = useBreakpointHelpers();

  const onCheckTerms = React.useCallback(
    (checked?: boolean) => {
      handleAcceptTerms(checked ?? false);
    },
    [handleAcceptTerms],
  );

  const price = 1000; // TODO: Fetch from the actual plan
  const parsedTermsAndConditions = content?.termsAndConditions
    ?.replace('{date}', formatDate(new Date()))
    ?.replace('{price}', formatPrice(price, 'USD'));

  return (
    <CheckoutLayoutBlock testID={testID}>
      <SectionHeader heading={t('general.your-order')} />

      <VStack
        customSpacing={0}
        divider={true}
        dividerColor={theme.colors.LIGHT}
      >
        <View style={styles.lineItem}>
          <BodyText bold>Sweetpass+</BodyText>
          <BodyText>{formatPrice(price, 'USD')}/mo</BodyText>
        </View>
      </VStack>

      <VStack customSpacing={theme.spacing.medium} style={styles.topBorder}>
        <Checkbox
          testID="sweetpass.checkout.accept"
          style={styles.checkbox}
          checked={hasAcceptedTerms}
          onChange={onCheckTerms}
          label={parsedTermsAndConditions}
          bold
        />
        {isMobile ? null : (
          <>
            <SweetpassCheckoutCta
              testID="sweetpass.checkout.cta-desktop"
              handleCta={handleCta}
              isLoading={isLoading}
              hasAccepted={hasAcceptedTerms}
            />
            <BodyText style={styles.upgradeNotice} size={5}>
              <TextLinkifyTags
                size={5}
                links={[
                  {
                    tag: 'terms',
                    description: t(
                      'sweetpass.checkout.upgrade-cta-notice.terms-description',
                    ),
                    href: t('sweetpass-upgrade.confirm-modal.terms-url'),
                    newTab: true,
                  },
                ]}
              >
                {t('sweetpass.checkout.upgrade-cta-notice')}
              </TextLinkifyTags>
            </BodyText>
          </>
        )}
      </VStack>
    </CheckoutLayoutBlock>
  );
};

// ─── TYPES ─────────────────────────────────────────────────────────────────────

type SweetpassCheckoutBlockProps = {
  readonly content: ReturnType<
    typeof useSweetpassCheckoutLoadedContent
  >['content'];
  readonly isLoading: boolean;
  readonly hasAcceptedTerms: boolean;
  readonly handleAcceptTerms: (hasAcceptedTerms: boolean) => void;
  readonly handleCta: () => void;
} & Pick<ViewProps, 'testID'>;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  topBorder: {
    paddingTop: theme.spacing.large,
    borderTopWidth: 1,
    borderTopColor: theme.colors.TEXT_COLOR,
  },
  lineItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.xlarge,
  },
  checkbox: {
    alignItems: 'flex-start',
  },
  upgradeNotice: {
    paddingTop: theme.spacing.small,
    textAlign: 'center',
    color: theme.colors.NEUTRAL_2,
  },
});
